import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryForm from '../../components/obituary/ObituaryForm';
import ProgressBar from '../../components/common/ProgressBar';
import Button from '../../components/common/Button';

/**
 * ObituaryCreateStep1 - 부고 작성 1단계 (고인 정보 입력)
 * 모바일 전용 다단계 부고 작성 페이지
 */
const ObituaryCreateStep1 = () => {
  const navigate = useNavigate();

  // localStorage에서 기존 데이터 불러오기
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('obituaryDraft');
    return saved ? JSON.parse(saved) : {
      deceasedName: '',
      deceasedBirthDate: '',
      deceasedDeathDate: '',
      relationship: ''
    };
  });

  const [errors, setErrors] = useState({});

  // 임시저장(이어서 작성) 안내: 마운트 시 저장된 draft가 있고 내용이 있으면 표시
  const [showResumeNotice, setShowResumeNotice] = useState(() => {
    const saved = localStorage.getItem('obituaryDraft');
    if (!saved) return false;
    try {
      const parsed = JSON.parse(saved);
      return Object.values(parsed).some((v) => v && v !== '');
    } catch (e) {
      return false;
    }
  });

  // 새로 작성 시작 (임시저장 삭제)
  const handleStartFresh = () => {
    localStorage.removeItem('obituaryDraft');
    setFormData({
      deceasedName: '',
      deceasedBirthDate: '',
      deceasedDeathDate: '',
      relationship: ''
    });
    setShowResumeNotice(false);
  };

  // 폼 데이터 변경 핸들러
  const handleChange = (newData) => {
    setFormData(newData);
    // localStorage에 임시 저장
    localStorage.setItem('obituaryDraft', JSON.stringify(newData));
    // 에러 초기화
    if (errors[Object.keys(newData).find(key => newData[key] !== formData[key])]) {
      setErrors({});
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    if (!formData.deceasedName?.trim()) {
      newErrors.deceasedName = '고인명을 입력해주세요.';
    }

    if (!formData.deceasedBirthDate) {
      newErrors.deceasedBirthDate = '생년월일을 입력해주세요.';
    }

    if (!formData.deceasedDeathDate) {
      newErrors.deceasedDeathDate = '사망일을 입력해주세요.';
    }

    if (!formData.relationship) {
      newErrors.relationship = '고인과의 관계를 선택해주세요.';
    }

    // 생년월일이 사망일보다 이후인 경우
    if (formData.deceasedBirthDate && formData.deceasedDeathDate) {
      if (new Date(formData.deceasedBirthDate) > new Date(formData.deceasedDeathDate)) {
        newErrors.deceasedDeathDate = '사망일은 생년월일 이후여야 합니다.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 다음 단계로 이동
  const handleNext = () => {
    if (validateForm()) {
      navigate('/obituaries/create/step2');
    }
  };

  // 취소
  const handleCancel = () => {
    if (window.confirm('작성 중인 내용이 저장됩니다. 취소하시겠습니까?')) {
      navigate('/');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary-900 mb-2">
            부고 작성
          </h1>
          <p className="text-sm text-gray-600">
            고인의 정보를 입력해주세요.
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar
          currentStep={1}
          totalSteps={3}
          steps={['고인 정보', '장례 정보', '상주 정보']}
          className="mb-8"
        />

        {/* 임시저장 복원 안내 */}
        {showResumeNotice && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
            <p className="text-sm text-primary-800">
              임시저장된 작성 내용을 불러왔습니다. 이어서 작성하시겠어요?
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleStartFresh}>
                새로 작성
              </Button>
              <Button variant="primary" size="sm" onClick={() => setShowResumeNotice(false)}>
                이어서 작성
              </Button>
            </div>
          </div>
        )}

        {/* Form */}
        <ObituaryForm
          formData={formData}
          onChange={handleChange}
          errors={errors}
          step="deceased"
        />

        {/* Actions */}
        <div className="flex gap-3 mt-8">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1"
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleNext}
            className="flex-1"
          >
            다음
          </Button>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">안내:</span> 작성 중인 내용은 자동으로 임시 저장됩니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituaryCreateStep1;
